import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Banknote,
  ChevronRight,
  ArrowRight,
  CheckCircle2,
  Clock,
  Shield,
  Smartphone,
  Gift,
  Share2,
  Star,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  DollarSign,
  UserPlus,
  Send,
  Wallet,
  BadgeCheck,
  Landmark,
  Lock,
  Calculator,
  AlertTriangle,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useMemo, useState } from "react";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Calculator", href: "#calculator" },
  { label: "Refer & Earn", href: "#referral" },
  { label: "Loans", href: "#loans" },
  { label: "FAQ", href: "#faq" },
];

const loanProducts = [
  {
    name: "Quick Cash",
    amount: "KES 1,000 – 10,000",
    rate: "From 5%",
    tenure: "7 – 30 days",
    color: "from-primary to-primary/80",
    popular: false,
  },
  {
    name: "Salary Advance",
    amount: "KES 5,000 – 50,000",
    rate: "From 8%",
    tenure: "14 – 60 days",
    color: "from-success to-success/80",
    popular: true,
  },
  {
    name: "Business Boost",
    amount: "KES 10,000 – 200,000",
    rate: "From 10%",
    tenure: "1 – 6 months",
    color: "from-info to-info/80",
    popular: false,
  },
];

const howItWorks = [
  {
    icon: UserPlus,
    step: "01",
    title: "Sign Up",
    desc: "Create your account in under 2 minutes with your phone number and ID.",
  },
  {
    icon: Smartphone,
    step: "02",
    title: "Apply",
    desc: "Choose your product, enter the amount, and submit in a few taps.",
  },
  {
    icon: Clock,
    step: "03",
    title: "Get Reviewed",
    desc: "Our smart checks review your eligibility quickly and securely.",
  },
  {
    icon: Wallet,
    step: "04",
    title: "Receive Cash",
    desc: "Approved funds are sent straight to your M-Pesa in minutes.",
  },
];

const referralSteps = [
  {
    icon: Share2,
    title: "Share Your Link",
    desc: "Copy your unique referral code and send it to friends and family.",
  },
  {
    icon: UserPlus,
    title: "Friend Applies",
    desc: "When your friend registers and gets their first loan approved.",
  },
  {
    icon: Banknote,
    title: "Earn Instantly",
    desc: "Receive KES 500 directly to your M-Pesa. No referral limits.",
  },
];

const testimonials = [
  {
    name: "Mary Njeri",
    text: "I got my loan in 3 minutes. Very smooth process and the money reflected fast on M-Pesa.",
    amount: "KES 15,000",
    avatar: "MN",
    meta: "Verified Borrower • Nairobi",
  },
  {
    name: "Peter Kamau",
    text: "The referral feature is excellent. I referred friends and received bonuses without any delays.",
    amount: "KES 6,000 earned",
    avatar: "PK",
    meta: "Verified User • Thika",
  },
  {
    name: "Fatuma Ali",
    text: "As a small business owner, quick top-ups help me restock fast when I need urgent working capital.",
    amount: "KES 50,000",
    avatar: "FA",
    meta: "Business Owner • Mombasa",
  },
  {
    name: "John Otieno",
    text: "Easy to use, clean interface, and I like seeing everything clearly before submitting the loan.",
    amount: "KES 4,500 earned",
    avatar: "JO",
    meta: "Verified User • Kisumu",
  },
];

const faqs = [
  {
    q: "How fast can I get a loan?",
    a: "Most eligible applications are reviewed quickly and disbursed to M-Pesa within minutes.",
  },
  {
    q: "What do I need to apply?",
    a: "You typically need your national ID, active phone number, and M-Pesa account details.",
  },
  {
    q: "How does the referral program work?",
    a: "Share your referral link. When someone signs up and gets approved, you receive KES 500.",
  },
  {
    q: "Can I apply for another loan?",
    a: "Yes, after your current loan is cleared. A good repayment history can improve your limit over time.",
  },
  {
    q: "Are there penalties for late payment?",
    a: "Late fees may apply after the grace period. Contact support early if you expect repayment difficulties.",
  },
];

const trustBadges = [
  { icon: Landmark, label: "CBK Licensed" },
  { icon: Lock, label: "256-bit SSL Secure" },
  { icon: BadgeCheck, label: "Trusted by 50,000+ Users" },
  { icon: CheckCircle2, label: "No Hidden Charges" },
];

const proofStrip = [
  "M-Pesa Ready",
  "CBK Licensed",
  "SSL Secure",
  "Fast Review",
  "Instant Disbursement",
];

export default function LandingPage() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [loanAmount, setLoanAmount] = useState(10000);
  const [loanDays, setLoanDays] = useState(30);

  const prefersReducedMotion = useReducedMotion();

  const fadeUp = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 24 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.2 },
        transition: { duration: 0.55 },
      };

  const staggerItem = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 18 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.2 },
        transition: { duration: 0.45 },
      };

  const scrollTo = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenu(false);
  };

  const estimatedRate = useMemo(() => {
    if (loanAmount <= 10000) return 0.05;
    if (loanAmount <= 50000) return 0.08;
    return 0.1;
  }, [loanAmount]);

  const estimatedInterest = useMemo(() => {
    const timeFactor = loanDays / 30;
    return Math.round(loanAmount * estimatedRate * timeFactor);
  }, [loanAmount, estimatedRate, loanDays]);

  const estimatedRepayment = useMemo(() => {
    return loanAmount + estimatedInterest;
  }, [loanAmount, estimatedInterest]);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, x: -16 }}
              animate={prefersReducedMotion ? {} : { opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-primary to-success flex items-center justify-center shadow-lg shadow-primary/20">
                <Banknote className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="leading-tight">
                <span className="text-lg sm:text-xl font-extrabold text-foreground block">
                  Pesa<span className="text-primary">Flash</span>
                </span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  Fast mobile credit
                </span>
              </div>
            </motion.div>

            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  {l.label}
                </button>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  size="sm"
                  className="gap-1 bg-gradient-to-r from-primary to-success border-0 hover:opacity-90 shadow-md"
                >
                  Register Now <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>

            <button
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl border border-border bg-card text-foreground"
              onClick={() => setMobileMenu((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {mobileMenu && (
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, height: 0 }}
              animate={prefersReducedMotion ? {} : { opacity: 1, height: "auto" }}
              className="md:hidden pb-4 space-y-2"
            >
              {navLinks.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className="block w-full text-left px-3 py-3 rounded-xl text-sm text-muted-foreground hover:text-primary hover:bg-muted/50"
                >
                  {l.label}
                </button>
              ))}
              <div className="grid grid-cols-2 gap-2 pt-2 px-1">
                <Link to="/login" className="w-full">
                  <Button variant="outline" size="sm" className="w-full">
                    Login
                  </Button>
                </Link>
                <Link to="/login" className="w-full">
                  <Button size="sm" className="w-full bg-gradient-to-r from-primary to-success border-0">
                    Apply Now
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section id="home" className="pt-24 sm:pt-28 pb-16 sm:pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(var(--primary),0.08),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(34,197,94,0.08),transparent_30%)]" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <motion.div {...fadeUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 text-success text-sm font-bold mb-5 border border-success/20">
                <Gift className="h-4 w-4" />
                Refer a friend and earn KES 500 instantly
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-foreground max-w-3xl">
                Get up to{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-success">
                  KES 50,000
                </span>{" "}
                in minutes, straight to{" "}
                <span className="text-success">M-Pesa</span>
              </h1>

              <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                Fast mobile loans with a simple application flow, secure verification,
                and quick disbursement. No paperwork. No long queues. Clear terms before you submit.
              </p>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-2.5 sm:gap-3 mt-6">
                {trustBadges.map((item) => (
                  <div
                    key={item.label}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-3 py-2 text-xs sm:text-sm text-muted-foreground shadow-sm"
                  >
                    <item.icon className="h-4 w-4 text-primary" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link to="/login" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="w-full sm:w-auto gap-2 text-base font-bold bg-gradient-to-r from-primary to-success border-0 hover:opacity-90 shadow-lg shadow-primary/20"
                  >
                    Apply for a Loan <ChevronRight className="h-4 w-4" />
                  </Button>
                </Link>

                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto text-base gap-2"
                  onClick={() => scrollTo("#calculator")}
                >
                  <Calculator className="h-4 w-4" />
                  Check Repayment
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-8 max-w-xl">
                {[
                  { label: "Approval", value: "< 5 min" },
                  { label: "Limit", value: "Up to 50K" },
                  { label: "Disbursement", value: "M-Pesa" },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-border bg-card/70 p-3 sm:p-4">
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                    <p className="mt-1 text-sm sm:text-base font-bold text-foreground">{item.value}</p>
                  </div>
                ))}
              </div>

              <p className="text-xs sm:text-sm text-muted-foreground mt-5 max-w-xl">
                Loan approval depends on eligibility checks and risk assessment. Interest rates,
                limits, and loan terms may vary by customer profile.
              </p>
            </motion.div>

            {/* Hero visual */}
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, x: 30 }}
              whileInView={prefersReducedMotion ? {} : { opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative hidden md:block"
            >
              <div className="relative mx-auto w-[320px] lg:w-[360px]">
                <div className="rounded-[2.75rem] border-[8px] border-foreground/10 bg-card shadow-2xl overflow-hidden">
                  <div className="bg-gradient-to-br from-primary via-primary/90 to-success p-6 pb-8 text-center">
                    <div className="w-16 h-1.5 rounded-full bg-primary-foreground/30 mx-auto mb-6" />
                    <Banknote className="h-10 w-10 text-primary-foreground mx-auto mb-3" />
                    <p className="text-primary-foreground/80 text-sm">Available Limit</p>
                    <p className="text-3xl lg:text-4xl font-black text-primary-foreground mt-1">
                      KES 50,000
                    </p>
                    <p className="text-primary-foreground/75 text-xs mt-2">
                      Fast review. Clear repayment terms. Secure process.
                    </p>
                  </div>

                  <div className="p-5 space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                      <span className="text-sm text-muted-foreground">Application Status</span>
                      <span className="text-xs font-bold text-success bg-success/10 px-2.5 py-1 rounded-full">
                        Approved
                      </span>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                      <span className="text-sm text-muted-foreground">Disbursement</span>
                      <span className="text-sm font-semibold text-foreground">M-Pesa</span>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                      <span className="text-sm text-muted-foreground">Processing Time</span>
                      <span className="text-sm font-semibold text-foreground">3 min</span>
                    </div>

                    <Link to="/login">
                      <Button className="w-full mt-2 bg-gradient-to-r from-primary to-success border-0 font-bold">
                        Apply Now
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>

              {!prefersReducedMotion && (
                <>
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                    className="absolute -left-3 top-20 bg-card rounded-2xl shadow-lg px-4 py-3 border border-border"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="h-9 w-9 rounded-full bg-success/15 flex items-center justify-center">
                        <DollarSign className="h-4 w-4 text-success" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Referral Bonus</p>
                        <p className="text-sm font-bold text-success">+ KES 500</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut", delay: 0.5 }}
                    className="absolute -right-3 bottom-24 bg-card rounded-2xl shadow-lg px-4 py-3 border border-border"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="h-9 w-9 rounded-full bg-primary/15 flex items-center justify-center">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Security</p>
                        <p className="text-sm font-bold text-foreground">Encrypted</p>
                      </div>
                    </div>
                  </motion.div>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Proof strip / logos */}
      <section className="py-5 bg-muted/30 border-y border-border/60">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {proofStrip.map((item) => (
              <div
                key={item}
                className="rounded-2xl border border-border/70 bg-card/70 px-4 py-3 text-center text-sm font-semibold text-muted-foreground"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="py-8 bg-background">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-center gap-8 sm:gap-14 items-center">
          {[
            { val: "50K+", label: "Active Borrowers" },
            { val: "KES 2B+", label: "Total Disbursed" },
            { val: "< 5 min", label: "Avg. Review Time" },
            { val: "4.8 ★", label: "User Rating" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-xl sm:text-2xl font-extrabold text-primary">{s.val}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Loan Calculator */}
      <section id="calculator" className="py-16 sm:py-20 px-4 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-primary font-bold text-sm uppercase tracking-wider">
              Loan Calculator
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-foreground">
              See your estimated repayment before you apply
            </h2>
            <p className="text-muted-foreground mt-4">
              Transparent estimates help users understand repayment early and improve trust.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
            <Card className="border-border/60 shadow-card">
              <CardContent className="p-5 sm:p-6">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">
                      Loan Amount
                    </label>
                    <Input
                      type="number"
                      min={1000}
                      max={200000}
                      step={500}
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(Number(e.target.value || 0))}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Enter amount between KES 1,000 and KES 200,000
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">
                      Repayment Period (Days)
                    </label>
                    <Input
                      type="number"
                      min={7}
                      max={180}
                      step={1}
                      value={loanDays}
                      onChange={(e) => setLoanDays(Number(e.target.value || 0))}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Shorter periods reduce total borrowing cost
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="rounded-2xl border border-border bg-muted/40 p-4">
                    <p className="text-xs text-muted-foreground">Estimated Rate</p>
                    <p className="mt-1 text-lg font-bold text-foreground">
                      {(estimatedRate * 100).toFixed(0)}%
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border bg-muted/40 p-4">
                    <p className="text-xs text-muted-foreground">Estimated Interest</p>
                    <p className="mt-1 text-lg font-bold text-foreground">
                      KES {estimatedInterest.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
                    <p className="text-xs text-muted-foreground">Estimated Repayment</p>
                    <p className="mt-1 text-lg font-black text-primary">
                      KES {estimatedRepayment.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <Link to="/login" className="w-full sm:w-auto">
                    <Button className="w-full sm:w-auto bg-gradient-to-r from-primary to-success border-0">
                      Apply With This Amount
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      setLoanAmount(10000);
                      setLoanDays(30);
                    }}
                  >
                    Reset Calculator
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/60 shadow-card bg-gradient-to-br from-card to-primary/5">
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <h3 className="font-bold text-foreground">Important Notice</h3>
                </div>

                <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                  <p>
                    This calculator provides an estimate only. Final approval, interest rate,
                    fees, loan duration, and amount may vary after eligibility checks.
                  </p>
                  <p>
                    Always review the final loan offer and repayment schedule before accepting.
                  </p>
                  <p>
                    Late repayment may attract penalties after the applicable grace period.
                  </p>
                  <p>
                    Contact support if you need clarification on fees, repayment dates, or eligibility.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-primary font-bold text-sm uppercase tracking-wider">
              How It Works
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-foreground">
              4 simple steps to access funds
            </h2>
            <p className="text-muted-foreground mt-4">
              A clean, fast, and secure process built for mobile-first users.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step) => (
              <motion.div key={step.step} {...staggerItem}>
                <Card className="h-full shadow-card border-border/50 relative overflow-hidden group hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl font-black text-primary/10 absolute top-3 right-4 group-hover:text-primary/20 transition-colors">
                      {step.step}
                    </div>
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/15 to-success/15 flex items-center justify-center mx-auto mb-4">
                      <step.icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground">{step.title}</h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{step.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral */}
      <section id="referral" className="py-16 sm:py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-success/5 via-primary/5 to-transparent" />
        <div className="max-w-7xl mx-auto relative">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 text-success text-sm font-bold mb-4 border border-success/20">
              <Gift className="h-4 w-4" /> Referral Program
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
              Refer friends and get paid instantly
            </h2>
            <p className="text-muted-foreground mt-4 text-lg">
              Earn <span className="font-bold text-success">KES 500</span> for every successful referral.
              Simple, fast, and directly paid to M-Pesa.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {referralSteps.map((s) => (
              <motion.div key={s.title} {...staggerItem}>
                <div className="text-center">
                  <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary/15 to-success/15 flex items-center justify-center mx-auto mb-5 border-4 border-background">
                    <s.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">{s.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp}>
            <Card className="max-w-lg mx-auto shadow-xl border-success/20 bg-gradient-to-br from-card to-success/5">
              <CardContent className="p-6 text-center">
                <Gift className="h-10 w-10 text-success mx-auto mb-3" />
                <h3 className="text-xl font-bold text-foreground">
                  Referral Earnings Calculator
                </h3>
                <p className="text-muted-foreground text-sm mt-1">
                  Refer 10 friends = <span className="font-bold text-success">KES 5,000</span>
                </p>
                <div className="mt-5 flex flex-col sm:flex-row gap-2">
                  <Input placeholder="Enter your phone to get your link" className="flex-1" />
                  <Button className="bg-gradient-to-r from-primary to-success border-0 font-bold gap-1 hover:opacity-90">
                    <Send className="h-4 w-4" /> Get Link
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Loan Products */}
      <section id="loans" className="py-16 sm:py-20 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-primary font-bold text-sm uppercase tracking-wider">
              Loan Products
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-foreground">
              Choose the product that fits your need
            </h2>
            <p className="text-muted-foreground mt-4">
              Designed for salary access, emergencies, and business cash flow support.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {loanProducts.map((p) => (
              <motion.div key={p.name} {...staggerItem}>
                <Card
                  className={`h-full shadow-card border-border/50 overflow-hidden relative hover:-translate-y-1 hover:shadow-xl transition-all duration-300 ${
                    p.popular ? "ring-2 ring-success" : ""
                  }`}
                >
                  {p.popular && (
                    <div className="absolute top-0 right-0 bg-success text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">
                      Most Popular
                    </div>
                  )}

                  <div className={`bg-gradient-to-r ${p.color} p-6 text-center`}>
                    <h3 className="text-xl font-bold text-primary-foreground">{p.name}</h3>
                    <p className="text-2xl font-black text-primary-foreground mt-2">{p.amount}</p>
                  </div>

                  <CardContent className="p-6">
                    <div className="space-y-3">
                      {[
                        { label: "Interest Rate", value: p.rate },
                        { label: "Loan Period", value: p.tenure },
                        { label: "Disbursement", value: "Instant M-Pesa" },
                        { label: "Collateral", value: "Not Required" },
                      ].map((item) => (
                        <div key={item.label} className="flex justify-between items-center gap-3">
                          <span className="text-sm text-muted-foreground">{item.label}</span>
                          <span className="text-sm font-semibold text-foreground text-right">
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>

                    <Link to="/login">
                      <Button className="w-full mt-6 gap-1 font-bold" variant={p.popular ? "default" : "outline"}>
                        Apply Now <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <p className="text-xs sm:text-sm text-muted-foreground text-center mt-6 max-w-3xl mx-auto">
            Displayed products and rates are indicative. Final loan offer depends on eligibility,
            account history, internal checks, and risk profile.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-primary font-bold text-sm uppercase tracking-wider">
              Testimonials
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-foreground">
              Why users keep coming back
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t) => (
              <motion.div key={t.name} {...staggerItem}>
                <Card className="h-full shadow-card border-border/50 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="h-3.5 w-3.5 fill-warning text-warning" />
                      ))}
                    </div>

                    <p className="text-sm text-muted-foreground leading-relaxed">“{t.text}”</p>

                    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-success/20 flex items-center justify-center text-xs font-bold text-primary">
                        {t.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.meta}</p>
                        <p className="text-xs text-success font-medium mt-0.5">{t.amount}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20 px-4 bg-muted/30">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <span className="text-primary font-bold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-foreground">
              Common Questions
            </h2>
          </motion.div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <motion.div key={i} {...staggerItem}>
                <Card
                  className="shadow-card border-border/50 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="font-semibold text-foreground">{faq.q}</h3>
                      <ChevronRight
                        className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform ${
                          openFaq === i ? "rotate-90" : ""
                        }`}
                      />
                    </div>
                    {openFaq === i && (
                      <motion.p
                        initial={prefersReducedMotion ? false : { opacity: 0, height: 0 }}
                        animate={prefersReducedMotion ? {} : { opacity: 1, height: "auto" }}
                        className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border"
                      >
                        {faq.a}
                      </motion.p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-warning/30 bg-warning/5 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-warning mt-0.5 shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">Legal & Transparency</h3>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  Borrow responsibly. Final loan pricing, availability, tenure, fees, and eligibility
                  may vary. Users should review all loan terms before acceptance. Late repayment may
                  attract additional charges where applicable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            {...fadeUp}
            className="rounded-[2rem] bg-gradient-to-r from-primary via-primary/90 to-success p-8 sm:p-12 lg:p-14 text-center relative overflow-hidden shadow-2xl"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent)]" />
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-black text-primary-foreground">
                Ready for fast access to cash?
              </h2>
              <p className="text-primary-foreground/80 mt-4 max-w-2xl mx-auto text-base sm:text-lg">
                Join thousands of users already using PesaFlash for quick mobile credit,
                referral earnings, and smooth repayment visibility.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3 mt-8">
                <Link to="/login" className="w-full sm:w-auto">
                  <Button size="lg" variant="secondary" className="w-full sm:w-auto gap-2 text-base font-bold shadow-lg">
                    Apply Now <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto text-base border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 gap-2"
                  onClick={() => scrollTo("#referral")}
                >
                  <Gift className="h-4 w-4" /> Refer & Earn KES 500
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer - darker professional */}
      <footer className="bg-slate-950 text-slate-200 py-14 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-primary to-success flex items-center justify-center shadow-lg">
                  <Banknote className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <span className="text-lg font-extrabold text-white block">
                    PesaFlash
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400">
                    Fast mobile credit
                  </span>
                </div>
              </div>

              <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
                Quick loans, clean experience, secure verification, and direct M-Pesa
                disbursement built for modern mobile users.
              </p>

              <div className="flex gap-3 mt-5">
                {[Facebook, Twitter, Instagram].map((Icon, i) => (
                  <a
                    key={i}
                    href="#"
                    className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Quick Links</h4>
              <ul className="space-y-2.5 text-sm text-slate-400">
                {["Apply for Loan", "Loan Calculator", "Referral Program", "FAQs"].map((t) => (
                  <li key={t}>
                    <a href="#" className="hover:text-white transition-colors">
                      {t}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Contact</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-primary" /> +254 700 123 456
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-primary" /> help@pesaflash.co.ke
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" /> Nairobi, Kenya
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Compliance</h4>
              <ul className="space-y-2.5 text-sm text-slate-400">
                {["Privacy Policy", "Terms of Service", "Loan Terms", "Data Protection"].map((t) => (
                  <li key={t}>
                    <a href="#" className="hover:text-white transition-colors">
                      {t}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs text-slate-400 leading-relaxed">
                  Licensed lender. Final offers depend on eligibility checks, internal review,
                  and applicable lending regulations.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © 2026 PesaFlash. All rights reserved.
            </p>
            <div className="flex gap-4 text-sm text-slate-500">
              <a href="#" className="hover:text-white transition-colors">
                Privacy
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Terms
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-3 left-0 right-0 z-50 px-4 md:hidden">
        <div className="mx-auto max-w-md rounded-2xl border border-border/70 bg-background/90 backdrop-blur-xl shadow-2xl p-2">
          <div className="flex items-center gap-2">
            <Link to="/login" className="flex-1">
              <Button className="w-full h-12 text-sm font-bold bg-gradient-to-r from-primary to-success border-0 shadow-md">
                Apply Now
              </Button>
            </Link>
            <Button
              variant="outline"
              className="h-12 px-4"
              onClick={() => scrollTo("#calculator")}
            >
              <Calculator className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}